/**
 * The anchor alarm boundary as an editable shape.
 *
 * A swing circle is the right answer nearly everywhere: the boat lies to its
 * rode and sweeps a circle as the wind and tide turn it. What a circle cannot
 * say is that one side of that sweep has rocks in it and the other has half a
 * mile of clean water. A single radius has to be set for the worst bearing, so
 * either it is tight enough for the rocks and cries wolf all night on the open
 * side, or it is set for the open side and says nothing until we are on the
 * rocks (fb-msca383o-2).
 *
 * So the boundary starts AS the circle and is then edited: pull a point in on
 * the side that matters and leave the rest alone. Editing beats drawing here
 * because the circle is already correct nearly everywhere, and because a
 * skipper reshaping a boundary at anchor is doing it in the dark, in a hurry.
 *
 * Pure geometry, so all of it is testable without a map.
 */

const M_PER_NM = 1852;
const M_PER_DEG_LAT = 111_320;

/**
 * How many points a seeded circle gets.
 *
 * Six. This has come down twice: sixteen, then eight, and eight was still too
 * many (fb-msdph9hi-1). The count that matters is not the vertices, it is the
 * HANDLES on screen, and there are two per vertex once the add-a-point handles
 * between them are counted. Sixteen vertices put thirty-two dots on the ring,
 * which is what Edd was actually looking at, and it reads as a bead necklace
 * rather than a boundary with six places to grab.
 *
 * Six only works because the boundary is drawn as a curve through the points
 * rather than as straight lines between them (see zoneBoundaryRing). Straight
 * lines through six points cut the corners to 87% of the radius, which is a
 * nuisance alarm waiting to happen. The curve holds 97%, which is closer to the
 * true circle than the sixteen-point polygon managed.
 */
export const DEFAULT_ZONE_POINTS = 6;

/**
 * How many segments each span between two points is drawn with.
 *
 * Twelve is past the point where more stops being visible at anchor-watch zoom,
 * and a six-point boundary is still only 72 points to ray-cast against, which
 * is nothing next to how often a GNSS fix arrives.
 */
const CURVE_STEPS = 12;

/** Offset a lon/lat by metres east and north. */
function offsetMetres(p: [number, number], eastM: number, northM: number): [number, number] {
  const cos = Math.max(0.01, Math.cos((p[1] * Math.PI) / 180));
  return [p[0] + eastM / (M_PER_DEG_LAT * cos), p[1] + northM / M_PER_DEG_LAT];
}

/**
 * The swing circle as a ring of editable points, clockwise from due north.
 *
 * Eight by default, which is DEFAULT_ZONE_POINTS above and the reasoning is
 * there. Any count is allowed; three is the floor, since below that there is no
 * area for the alarm to test against.
 */
export function circleToZone(
  centre: [number, number],
  radiusNm: number,
  points: number = DEFAULT_ZONE_POINTS,
): [number, number][] {
  const n = Math.max(3, Math.floor(points));
  if (!(radiusNm > 0)) return [];
  const r = radiusNm * M_PER_NM;
  return Array.from({ length: n }, (_, i) => {
    const t = (i / n) * 2 * Math.PI;
    return offsetMetres(centre, Math.sin(t) * r, Math.cos(t) * r);
  });
}

/**
 * The boundary the skipper actually sees and the alarm actually tests: a closed
 * curve through the points rather than straight lines between them.
 *
 * Why curve it at all. The points are a handle count, and we want that count
 * low enough to work with a thumb in the dark. Straight lines between six
 * points cut the corners badly (down to 87% of the radius), and a boundary
 * tighter than the swing it came from is a nuisance alarm, which is how anchor
 * alarms end up switched off. A curve through the same six points holds 97%.
 *
 * Centripetal Catmull-Rom, which is the variant that will not tie itself in a
 * knot when two points are dragged close together. That is not a theoretical
 * worry here: pulling one side of the boundary hard in against a rock is
 * exactly the thing this feature exists for, and the uniform variant kinks and
 * loops back on itself when you do it.
 *
 * Returns a CLOSED ring (first point repeated at the end), ready for a polygon.
 */
export function zoneBoundaryRing(
  zone: [number, number][],
  steps: number = CURVE_STEPS,
): [number, number][] {
  const n = zone.length;
  if (n < 3) return zone.length ? [...zone] : [];
  const out: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    const p0 = zone[(i - 1 + n) % n];
    const p1 = zone[i];
    const p2 = zone[(i + 1) % n];
    const p3 = zone[(i + 2) % n];
    for (let k = 0; k < steps; k++) out.push(catmullRom(p0, p1, p2, p3, k / steps));
  }
  out.push(out[0]);
  return out;
}

/**
 * One point along the centripetal Catmull-Rom span between p1 and p2, with p0
 * and p3 setting the tangents. Barry-Goldman form, so it is the parameterisation
 * doing the work rather than a separate anti-kink fudge.
 */
function catmullRom(
  p0: [number, number],
  p1: [number, number],
  p2: [number, number],
  p3: [number, number],
  t: number,
): [number, number] {
  // Longitude degrees are shorter than latitude ones away from the equator, so
  // scale before measuring or the knots come out spaced by a squashed distance
  // and the curve leans east-west. Same correction distanceToSegment makes.
  const k = Math.max(0.01, Math.cos((p1[1] * Math.PI) / 180));
  // The knots are spaced by the SQUARE ROOT of the distance between points
  // (alpha = 0.5). Spacing them evenly instead is the uniform variant, which
  // overshoots and self-intersects around a tight cluster of points.
  const knot = (ti: number, a: [number, number], b: [number, number]) => {
    const d = Math.hypot((b[0] - a[0]) * k, b[1] - a[1]);
    // Two points in the same place would give a zero-length knot span and a
    // divide-by-zero further down. Nudge rather than refuse: a doubled-up point
    // is a boundary someone is mid-drag on, not an error.
    return ti + Math.max(Math.sqrt(d), 1e-9);
  };
  const t0 = 0;
  const t1 = knot(t0, p0, p1);
  const t2 = knot(t1, p1, p2);
  const t3 = knot(t2, p2, p3);
  const tt = t1 + (t2 - t1) * t;
  const lerp = (
    a: [number, number],
    b: [number, number],
    ta: number,
    tb: number,
  ): [number, number] => {
    if (tb === ta) return a;
    const w = (tb - tt) / (tb - ta);
    const v = (tt - ta) / (tb - ta);
    return [a[0] * w + b[0] * v, a[1] * w + b[1] * v];
  };
  const a1 = lerp(p0, p1, t0, t1);
  const a2 = lerp(p1, p2, t1, t2);
  const a3 = lerp(p2, p3, t2, t3);
  const b1 = lerp(a1, a2, t0, t2);
  const b2 = lerp(a2, a3, t1, t3);
  return lerp(b1, b2, t1, t2);
}

/**
 * Where the add-a-point handle for the span after `index` sits.
 *
 * On the CURVE, not on the straight line between the two points. A handle
 * floating off the boundary it claims to belong to is the sort of thing that
 * makes people stop trusting the whole control.
 */
export function edgeMidpoint(zone: [number, number][], index: number): [number, number] | null {
  if (zone.length < 2 || index < 0 || index >= zone.length) return null;
  const n = zone.length;
  const a = zone[index];
  const b = zone[(index + 1) % n];
  if (n < 3) return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  return catmullRom(zone[(index - 1 + n) % n], a, b, zone[(index + 2) % n], 0.5);
}

/**
 * Has this boundary never actually been shaped? True when every point still
 * sits the same distance from the anchor, which is a circle and nothing more.
 *
 * The boundary is persisted, quite deliberately, so an overnight watch survives
 * Android reaping the app. That also means a boundary seeded under an older
 * version keeps whatever point count it was born with, and the point count has
 * now come down twice. Re-seeding one that has never been touched costs nobody
 * anything. Re-seeding one that HAS been shaped would throw away the work of
 * pulling it off the rocks, so that is left exactly alone.
 */
export function zoneIsPlainCircle(
  zone: [number, number][],
  centre: [number, number],
  tolerance = 0.02,
): boolean {
  if (zone.length < 3) return false;
  const k = Math.max(0.01, Math.cos((centre[1] * Math.PI) / 180));
  const radii = zone.map((p) => Math.hypot((p[0] - centre[0]) * k, p[1] - centre[1]));
  const mean = radii.reduce((a, b) => a + b, 0) / radii.length;
  if (!(mean > 0)) return false;
  return radii.every((r) => Math.abs(r - mean) / mean <= tolerance);
}

/**
 * Move a whole boundary by the same amount the anchor moved.
 *
 * The boundary is drawn around the anchor and means nothing without it, but it
 * is stored as plain lon/lat, so re-placing the anchor used to leave the shape
 * sitting over the old spot. On screen that reads as the anchor having landed
 * somewhere other than where it was put, because the big obvious ring on the
 * chart has not moved (fb-msdpfn95-0). Worse, the alarm goes on watching a
 * boundary around water the boat is no longer anchored in.
 *
 * Re-placing the anchor is nearly always CORRECTING it (that is the whole point
 * of placing it by bearing or by tapping the chart), so carrying the shape
 * across is what is wanted. Dragging it along keeps the side we pulled in on
 * the same side of the anchor it was pulled in on.
 */
export function translateZone(
  zone: [number, number][],
  from: [number, number],
  to: [number, number],
): [number, number][] {
  const dLon = to[0] - from[0];
  const dLat = to[1] - from[1];
  if (dLon === 0 && dLat === 0) return zone;
  return zone.map((p) => [p[0] + dLon, p[1] + dLat] as [number, number]);
}

/**
 * Which edge a point lies nearest, for the long-press-to-insert action: the
 * index of the point the new one should go AFTER. Null when the zone is too
 * small to have edges.
 */
export function nearestEdgeIndex(zone: [number, number][], p: [number, number]): number | null {
  if (zone.length < 2) return null;
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i < zone.length; i++) {
    const d = distanceToSegment(p, zone[i], zone[(i + 1) % zone.length]);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

/**
 * Is the boat inside the boundary? Ray casting, on the boundary counting as in.
 *
 * The direction of the error matters more than the method. A boat exactly on
 * the line is not yet dragging, and waking the crew for a boat that has not
 * moved is how an anchor alarm ends up switched off, which is the outcome that
 * actually puts a boat on the rocks.
 */
export function insideZone(zone: [number, number][], p: [number, number]): boolean {
  if (zone.length < 3) return true; // no boundary to be outside of
  // The CURVE, not the points. What is drawn and what the alarm tests have to
  // be the same line, or the boat sits visibly inside its boundary with the
  // alarm going, and then nobody believes the alarm again.
  const ring = zoneBoundaryRing(zone);
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const straddles = yi > p[1] !== yj > p[1];
    if (straddles) {
      const xCross = ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi;
      if (p[0] < xCross) inside = !inside;
    }
  }
  return inside;
}

/** Perpendicular distance from p to the segment a-b, in degrees (comparison only). */
function distanceToSegment(
  p: [number, number],
  a: [number, number],
  b: [number, number],
): number {
  // Longitude degrees are shorter than latitude ones away from the equator, so
  // scale before comparing or every edge running east-west looks closer than it
  // is and the new point lands on the wrong side of the boat.
  const k = Math.max(0.01, Math.cos((a[1] * Math.PI) / 180));
  const px = p[0] * k;
  const ax = a[0] * k;
  const bx = b[0] * k;
  const dx = bx - ax;
  const dy = b[1] - a[1];
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(px - ax, p[1] - a[1]);
  let t = ((px - ax) * dx + (p[1] - a[1]) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + t * dx), p[1] - (a[1] + t * dy));
}
